import { t } from "@redotech/redo-i18n";
import { Condition } from "@redotech/redo-return-core-common/return-flow/condition";
import { NumReturnsOption } from "@redotech/redo-return-core-common/return-flow/condition-enums";
import { CONDITION_NAMES } from "@redotech/redo-return-core-common/return-flow/condition-names";
import {
  emptyDateSubconditionState,
  numCustomerReturnsFromModel,
  validateNumCustomerReturnsCondition,
  type NumCustomerReturnsState,
} from "@redotech/redo-return-core-common/return-flow/condition-validate";
import * as React from "react";
import { ConditionType, ConditionTypeDetailsProps } from "../condition";
import {
  getDateSubconditionSuffix,
  toModelDateSubcondition,
} from "./date-subcondition";

export const NUM_CUSTOMER_RETURNS = (
  Details: React.ComponentType<
    ConditionTypeDetailsProps<NumCustomerReturnsState>
  >,
): ConditionType<NumCustomerReturnsState, Condition.NumCustomerReturns> => ({
  name: CONDITION_NAMES[Condition.NUM_CUSTOMER_RETURNS],
  description(state) {
    if (!state.returnOption) {
      return t(
        "The total number of returns this customer has made across all their orders",
      );
    }

    const description =
      state.returnOption === NumReturnsOption.LESS_THAN
        ? t("Total customer returns across all orders less than {{value}}", {
            value: state.numReturns,
          })
        : t("Total customer returns across all orders greater than {{value}}", {
            value: state.numReturns,
          });

    return description + getDateSubconditionSuffix(state.dateSubcondition);
  },
  Details(props: ConditionTypeDetailsProps<NumCustomerReturnsState>) {
    return React.createElement(Details, props);
  },
  empty: {
    numReturns: (0).toFixed(0),
    returnOption: null,
    dateSubcondition: emptyDateSubconditionState,
  },
  fromModel(model) {
    return numCustomerReturnsFromModel(model);
  },
  toModel(state) {
    return {
      type: Condition.NUM_CUSTOMER_RETURNS,
      numReturns: +state.numReturns,
      numReturnsOption: state.returnOption,
      // Use only dateSubcondition going forward
      dateSubcondition: toModelDateSubcondition(state.dateSubcondition),
    };
  },
  valid(state) {
    return validateNumCustomerReturnsCondition(state).valid;
  },
});
